/**
 * Print module — types shared by service, dialog and rasterizer.
 *
 * Centralised so future job kinds (canvas snapshots for Taktik/Whiteboard,
 * TipTap docs for Notizen, plain HTML for reports) plug in through the
 * same dialog UI without changing call sites.
 */

export type PaperSize = "A3" | "A4" | "A5" | "Letter" | "Legal";
export type Orientation = "portrait" | "landscape";
export type MarginPreset = "narrow" | "normal" | "wide" | "none";

export interface PrintSettings {
  paper: PaperSize;
  orientation: Orientation;
  margin: MarginPreset;
  /** Scale percent applied to the rendered content. 100 = fit-to-page. */
  scalePct: number;
  /**
   * Grayscale hint — applied via CSS filter for image jobs. For PDF jobs
   * this is currently ignored (use the OS-level B&W toggle in the system
   * print dialog instead) because pdf-lib can't post-filter embedded
   * vector content.
   */
  grayscale: boolean;
  /**
   * Page selection — only meaningful for `pdf` jobs. Either "all", "current"
   * (the active page in the viewer), or a parsed list of 1-based numbers.
   */
  pageMode: "all" | "current" | "range";
  /** Raw "1-3, 5, 7-9" text — parsed lazily. */
  pageRangeText: string;
}

export type PrintJob =
  | { kind: "image"; src: string }
  | {
      kind: "pdf";
      /** Already-fetched URL (blob: or signed). PDF.js loads from this. */
      src: string;
      /** Total page count — used to enable/disable range UI. */
      totalPages: number;
      /** 1-based currently visible page (for "current" mode default). */
      currentPage: number;
    }
  | { kind: "html"; html: string };

export interface PrintRequest {
  /** Document title (used as window title in print dialog). */
  title: string;
  job: PrintJob;
  /** Optional initial overrides for the dialog defaults. */
  defaults?: Partial<PrintSettings>;
}

export const DEFAULT_SETTINGS: PrintSettings = {
  paper: "A4",
  orientation: "portrait",
  margin: "normal",
  scalePct: 100,
  grayscale: false,
  pageMode: "all",
  pageRangeText: "",
};

/**
 * Parse a user-entered range like "1-3, 5, 7-9" into sorted, unique,
 * 1-based page numbers. Silently drops invalid tokens (the dialog's
 * inline hint covers user feedback).
 */
export function parsePageRange(text: string, max: number): number[] {
  const out = new Set<number>();
  for (const raw of text.split(",")) {
    const t = raw.trim();
    if (!t) continue;
    const dash = t.indexOf("-");
    if (dash > 0) {
      const a = Number.parseInt(t.slice(0, dash), 10);
      const b = Number.parseInt(t.slice(dash + 1), 10);
      if (Number.isFinite(a) && Number.isFinite(b)) {
        const lo = Math.max(1, Math.min(a, b));
        const hi = Math.min(max, Math.max(a, b));
        for (let i = lo; i <= hi; i++) out.add(i);
      }
    } else {
      const n = Number.parseInt(t, 10);
      if (Number.isFinite(n) && n >= 1 && n <= max) out.add(n);
    }
  }
  return Array.from(out).sort((a, b) => a - b);
}

/** Map margin preset to a CSS length string for `@page { margin }`. */
export function marginCss(m: MarginPreset): string {
  switch (m) {
    case "none":
      return "0";
    case "narrow":
      return "8mm";
    case "wide":
      return "25mm";
    case "normal":
    default:
      return "15mm";
  }
}

/** Map paper preset to a CSS `size` value for `@page`. */
export function paperCss(p: PaperSize, o: Orientation): string {
  // CSS supports named sizes for A3..A5/Letter/Legal. Adding `landscape`
  // rotates the page in the browser's print pipeline.
  return `${p} ${o}`;
}
